import React, { useEffect, useRef, useState } from 'react'

const formatNumericValue = (value, precision = 2) => {
    const number = Number(value)

    if (!Number.isFinite(number)) return ''

    return number
        .toFixed(Math.max(0, Number(precision) || 0))
        .replace(/\.0+$/, '')
        .replace(/(\.\d*?)0+$/, '$1')
}

const parseNumericValue = (value) => {
    const normalized = String(value ?? '')
        .trim()
        .replace(',', '.')

    if (!normalized || normalized === '-' || normalized === '.' || normalized === '-.') {
        return null
    }

    const number = Number(normalized)

    return Number.isFinite(number) ? number : null
}

const DeferredNumericInput = ({
    value,
    onCommit,
    disabled = false,
    min,
    max,
    precision = 2,
    allowNegative = true,
    integer = false,
    inputMode = 'decimal',
    className = '',
    ariaLabel,
    title,
}) => {
    const focusedRef = useRef(false)
    const cancelBlurRef = useRef(false)
    const [draft, setDraft] = useState(() => formatNumericValue(value, precision))

    useEffect(() => {
        if (focusedRef.current) return
        const next = formatNumericValue(value, precision)
        setDraft((current) => current === next ? current : next)
    }, [precision, value])

    const resetDraft = () => {
        const next = formatNumericValue(value, precision)
        setDraft((current) => current === next ? current : next)
    }

    const commitDraft = () => {
        const parsed = parseNumericValue(draft)

        if (parsed === null) {
            resetDraft()
            return
        }

        let next = integer ? Math.round(parsed) : parsed
        const numericMin = Number(min)
        const numericMax = Number(max)

        if (Number.isFinite(numericMin)) next = Math.max(numericMin, next)
        if (Number.isFinite(numericMax)) next = Math.min(numericMax, next)

        setDraft(formatNumericValue(next, precision))
        onCommit?.(next)
    }

    const handleChange = (event) => {
        const next = event.target.value
        const pattern = allowNegative
            ? /^-?\d*(?:[.,]\d*)?$/
            : /^\d*(?:[.,]\d*)?$/

        if (next === '' || pattern.test(next)) {
            setDraft(next)
        }
    }

    const handleFocus = () => {
        focusedRef.current = true
        cancelBlurRef.current = false
    }

    const handleBlur = () => {
        focusedRef.current = false

        if (cancelBlurRef.current) {
            cancelBlurRef.current = false
            resetDraft()
            return
        }

        commitDraft()
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            event.currentTarget.blur()
            return
        }

        if (event.key === 'Escape') {
            event.preventDefault()
            cancelBlurRef.current = true
            resetDraft()
            event.currentTarget.blur()
        }
    }

    return (
        <input
            type="text"
            inputMode={integer ? 'numeric' : inputMode}
            value={draft}
            disabled={disabled}
            className={className}
            aria-label={ariaLabel}
            title={title}
            autoComplete="off"
            spellCheck="false"
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
        />
    )
}

export default React.memo(DeferredNumericInput)
